import React from 'react';

const Avatar = ({
  src,
  alt = 'Avatar',
  name,
  size = 'md',
  rounded = 'full',
  status,
  statusPosition = 'bottom-right',
  className = ''
}) => {
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
    '2xl': 'w-20 h-20 text-2xl'
  };
  
  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full'
  };
  
  const statusSizeClasses = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3',
    xl: 'w-3.5 h-3.5',
    '2xl': 'w-4 h-4'
  };
  
  const statusPositionClasses = {
    'top-right': 'top-0 right-0',
    'top-left': 'top-0 left-0',
    'bottom-right': 'bottom-0 right-0',
    'bottom-left': 'bottom-0 left-0'
  };
  
  const statusColorClasses = {
    online: 'bg-[var(--color-success)]',
    offline: 'bg-[var(--color-text-muted)]',
    busy: 'bg-[var(--color-error)]',
    away: 'bg-[var(--color-warning)]'
  };
  
  const getInitials = (name) => {
    if (!name) return '';
    const words = name.split(' ');
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };
  
  return (
    <div className={`relative inline-block ${className}`}>
      {src ? (
        <img
          src={src}
          alt={alt}
          className={`
            ${sizeClasses[size]}
            ${roundedClasses[rounded]}
            object-cover
          `}
        />
      ) : (
        <div className={`
          ${sizeClasses[size]}
          ${roundedClasses[rounded]}
          bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)]
          text-[var(--color-text-inverse)] font-semibold
          flex items-center justify-center
        `}>
          {name ? getInitials(name) : '?'}
        </div>
      )}
      
      {status && (
        <span className={`
          absolute ${statusPositionClasses[statusPosition]}
          ${statusSizeClasses[size]}
          ${statusColorClasses[status]}
          rounded-full
          border-2 border-[var(--color-surface)]
        `} />
      )}
    </div>
  );
};

const AvatarGroup = ({
  children,
  max = 3,
  size = 'md',
  className = ''
}) => {
  const childrenArray = React.Children.toArray(children);
  const visibleChildren = childrenArray.slice(0, max);
  const remainingCount = childrenArray.length - max;
  
  return (
    <div className={`flex -space-x-2 ${className}`}>
      {visibleChildren.map((child, index) => (
        <div key={index} className="relative" style={{ zIndex: max - index }}>
          {React.cloneElement(child, { size })}
        </div>
      ))}
      {remainingCount > 0 && (
        <div className={`
          relative
          ${size === 'xs' ? 'w-6 h-6 text-xs' :
            size === 'sm' ? 'w-8 h-8 text-sm' :
            size === 'md' ? 'w-10 h-10 text-base' :
            size === 'lg' ? 'w-12 h-12 text-lg' :
            size === 'xl' ? 'w-16 h-16 text-xl' :
            'w-20 h-20 text-2xl'}
          rounded-full
          bg-[var(--color-background-secondary)]
          text-[var(--color-text-secondary)]
          font-semibold
          flex items-center justify-center
          border-2 border-[var(--color-surface)]
        `}>
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

export { Avatar, AvatarGroup };
export default Avatar;
